import { useState, useEffect } from "react";
import {
	AppBar,
	Toolbar,
	Typography,
	Button,
	IconButton,
	Drawer,
	List,
	ListItem,
	ListItemText,
	Box,
	useMediaQuery,
	useTheme,
	Menu,
	MenuItem,
	Collapse,
	ListItemButton,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ExpandLess from "@mui/icons-material/ExpandLess";
import ExpandMore from "@mui/icons-material/ExpandMore";
import supabase from "../../supabaseClient";
import GosellGardensLogo from "../../Images/GosellGardensLogo.png";

const NavBar = () => {
	const [userData, setUserData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
	const [anchorEl, setAnchorEl] = useState(null);
	const [eggsOpen, setEggsOpen] = useState(false);

	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("md")); // sm = 600px

	// Base navigation items that always show
	const baseNavItems = [
		{ text: "Home", path: "/" },
		{ text: "Plants", path: "/plants" },
		{ text: "Farmstand", path: "/farmstand" },
		{ text: "Store", path: "/store" },
	];

	// Auth-specific navigation items
	const getAuthItems = () => {
		if (userData) {
			// User is logged in
			return [
				{ text: "Dashboard", path: "/dashboard" },
				{ text: "Logout", path: "/logout" },
			];
		} else {
			// User is not logged in
			return [{ text: "Login", path: "/login" }];
		}
	};

	const eggMenuItems = [
		{ text: "Egg Requests", path: "/egg-request-list" },
		{ text: "Egg Game", path: "/egg-game" },
		{ text: "About Our Flock", path: "/about-our-flock" },
	];

	useEffect(() => {
		// Initial fetch of user data
		const fetchUserData = async () => {
			const { data, error } = await supabase.auth.getUser();
			if (error) {
				console.error("Error fetching user data:", error.message);
			} else {
				setUserData(data.user);
			}
			setLoading(false);
		};

		fetchUserData();

		// Set up auth state change listener
		const { data: authListener } = supabase.auth.onAuthStateChange(
			(event, session) => {
				if (event === "SIGNED_IN" && session) {
					setUserData(session.user);
				} else if (event === "SIGNED_OUT") {
					setUserData(null);
				}
			}
		);

		// Clean up the listener when the component unmounts
		return () => {
			authListener.subscription.unsubscribe();
		};
	}, []);

	const handleNavigate = (path) => {
		window.location.href = path;
		setMobileMenuOpen(false);
	};

	// Get regular and auth navigation items
	const authItems = getAuthItems();

	return (
		<AppBar position="static">
			<Toolbar>
				<img src={GosellGardensLogo} alt="Logo" style={{ height: 40 }} />
				<Typography
					variant="h6"
					component="div"
					sx={{ mr: 2, cursor: "pointer" }}
					onClick={() => handleNavigate("/")}
				>
					Gosell Gardens
				</Typography>

				{/* Regular navigation items - on the left */}
				{!isMobile && (
					<Box sx={{ display: "flex", flexGrow: 1 }}>
						{baseNavItems
							.filter((item) => item.text !== "Egg Requests")
							.map((item) => (
								<Button
									key={item.text}
									color="inherit"
									onClick={() => handleNavigate(item.path)}
									sx={{ mr: 1 }}
								>
									{item.text}
								</Button>
							))}
						{/* Eggs dropdown */}
						<Box>
							<Button
								color="inherit"
								aria-controls={Boolean(anchorEl) ? "eggs-menu" : undefined}
								aria-haspopup="true"
								aria-expanded={Boolean(anchorEl) ? "true" : undefined}
								onClick={(e) => setAnchorEl(e.currentTarget)}
							>
								Eggs
							</Button>
							<Menu
								id="eggs-menu"
								anchorEl={anchorEl}
								open={Boolean(anchorEl)}
								onClose={() => setAnchorEl(null)}
							>
								{eggMenuItems.map((item) => (
									<MenuItem
										key={item.text}
										onClick={() => {
											handleNavigate(item.path);
											setAnchorEl(null);
										}}
									>
										{item.text}
									</MenuItem>
								))}
							</Menu>
						</Box>
					</Box>
				)}

				{/* Auth navigation items - on the right */}
				{!isMobile && (
					<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
						{!loading &&
							authItems.map((item) => (
								<Button
									key={item.text}
									color="inherit"
									onClick={() => handleNavigate(item.path)}
									sx={{ ml: 1 }}
								>
									{item.text}
								</Button>
							))}
					</Box>
				)}

				{/* Mobile hamburger icon */}
				{isMobile && (
					<Box
						sx={{ flexGrow: 1, display: "flex", justifyContent: "flex-end" }}
					>
						<IconButton
							edge="end"
							color="inherit"
							aria-label="menu"
							onClick={() => setMobileMenuOpen(true)}
						>
							<MenuIcon />
						</IconButton>
					</Box>
				)}

				{/* Mobile navigation drawer */}
				<Drawer
					anchor="right"
					open={mobileMenuOpen && isMobile}
					onClose={() => setMobileMenuOpen(false)}
				>
					<Box sx={{ width: 250 }} role="presentation">
						<List>
							{!loading &&
								baseNavItems
									.filter((item) => item.text !== "Egg Requests")
									.map((item) => (
										<ListItem
											button
											key={item.text}
											onClick={() => handleNavigate(item.path)}
										>
											<ListItemText primary={item.text} />
										</ListItem>
									))}
							{/* Eggs collapsible section */}
							<ListItemButton onClick={() => setEggsOpen((prev) => !prev)}>
								<ListItemText primary="Eggs" />
								{eggsOpen ? <ExpandLess /> : <ExpandMore />}
							</ListItemButton>
							<Collapse in={eggsOpen} timeout="auto" unmountOnExit>
								<List component="div" disablePadding>
									{eggMenuItems.map((item) => (
										<ListItem
											button
											key={item.text}
											onClick={() => handleNavigate(item.path)}
											sx={{ pl: 4 }}
										>
											<ListItemText primary={item.text} />
										</ListItem>
									))}
								</List>
							</Collapse>
							{/* Auth items */}
							{!loading &&
								authItems.map((item) => (
									<ListItem
										button
										key={item.text}
										onClick={() => handleNavigate(item.path)}
									>
										<ListItemText primary={item.text} />
									</ListItem>
								))}
						</List>
					</Box>
				</Drawer>
			</Toolbar>
		</AppBar>
	);
};

export default NavBar;
